import type {
  CreateMessageMutation,
  GetMessagesQuery,
  GetMessagesQueryVariables,
} from '#src/graphql/generated';
import type { ApolloCache } from '@apollo/client';

import { GET_MESSAGES } from '#src/graphql/queries/messages';

function byCreatedAtAsc(
  a: { createdAt: unknown },
  b: { createdAt: unknown },
): number {
  return (
    new Date(a.createdAt as string).getTime() -
    new Date(b.createdAt as string).getTime()
  );
}

/**
 * Inserts `createMessage` mutation result into the cached {@link GET_MESSAGES} result
 * for the given variables (must match the active `useQuery` for that view).
 */
export function mergeCreateMessageIntoGetMessagesCache(
  cache: ApolloCache,
  created: CreateMessageMutation['createMessage'],
  variables: GetMessagesQueryVariables,
): void {
  cache.updateQuery<GetMessagesQuery>(
    { query: GET_MESSAGES, variables },
    (existing) => {
      if (!existing?.messages) return existing;

      if (created.parentMessageId) {
        const parentId = created.parentMessageId;
        return {
          messages: existing.messages.map((root) => {
            if (root.id !== parentId) return root;
            const existingChildren = root.childMessages ?? [];
            if (existingChildren.some((c) => c.id === created.id)) {
              return root;
            }
            const childMessages = [...existingChildren, created].sort(
              byCreatedAtAsc,
            );
            return { ...root, childMessages };
          }),
        };
      }

      if (existing.messages.some((m) => m.id === created.id)) {
        return existing;
      }

      const messages = [...existing.messages, created].sort(byCreatedAtAsc);
      return { ...existing, messages };
    },
  );
}
